// Export helpers for the Students admin page.
// Both CSV and PDF run client-side off the already-filtered students array
// — no extra round-trip, no extra backend endpoint. The PDF route opens a
// print-friendly window and triggers the browser's print dialog so the
// user can "Save as PDF" (every modern browser supports this natively).
//
// Class resolution: the backend now returns `enrolled_class_ids` per student
// PLUS a master `classes` list. The export builds a class_id → {name, day, time}
// lookup and resolves each student's IDs to authoritative class info — same
// source of truth as the Students filter dropdown. Legacy `class_signatures`
// / `classes` string fields are retained as a fallback for archived rows.

// Build a Map<class_id, { name, day, time }> from the master classes array.
const buildClassLookup = (availableClasses) => {
  const map = new Map();
  for (const c of availableClasses || []) {
    if (!c || !c.id) continue;
    map.set(c.id, {
      name: (c.name || '').trim(),
      day: (c.day_of_week || '').trim(),
      time: (c.start_time || '').trim(),
    });
  }
  return map;
};

// Normalise one "class reference" into [{ name, day, time }] — returns an
// array because some legacy rows store multiple class names joined with a
// comma in the same string (e.g. "UPA 3 Hour Course, Senior Street Dance").
// Splitting on comma cleans that up so each class shows on its own line.
const normaliseClass = (c) => {
  if (!c) return [];
  if (typeof c === 'string') {
    return c.split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name, day: '', time: '' }));
  }
  const rawName = (c.name || c.class_name || '').trim();
  if (!rawName) return [];
  const day = (c.day_of_week || c.day || '').trim();
  const time = (c.start_time || c.time || '').trim();
  // Same comma-split for object form
  return rawName.split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => ({ name, day, time }));
};

// Return a de-duplicated list of { name, day, time } for the student.
// Prefers `enrolled_class_ids` resolved via the master class lookup (exact,
// register-accurate). Falls back to legacy `class_signatures` / `classes`
// / `bookings` so archived/imported rows without IDs still show something.
const studentClassList = (student, classLookup) => {
  let items = [];
  const pushAll = (arr) => { for (const c of arr) items.push(...normaliseClass(c)); };

  const ids = Array.isArray(student.enrolled_class_ids) ? student.enrolled_class_ids : [];
  if (ids.length && classLookup && classLookup.size) {
    for (const cid of ids) {
      const c = classLookup.get(cid);
      if (c && c.name) items.push({ name: c.name, day: c.day, time: c.time });
    }
  }

  // Fallbacks for legacy / imported rows with no enrolled_class_ids.
  if (!items.length) {
    if (Array.isArray(student.class_signatures) && student.class_signatures.length) {
      pushAll(student.class_signatures);
    } else if (Array.isArray(student.class_names) && student.class_names.length) {
      pushAll(student.class_names);
    } else if (Array.isArray(student.classes) && student.classes.length) {
      pushAll(student.classes);
    } else if (Array.isArray(student.bookings) && student.bookings.length) {
      pushAll(
        student.bookings
          .filter((b) => b.payment_status === 'paid' || b.status === 'paid' || b.status === 'active')
          .map((b) => ({ name: b.class_name, day_of_week: b.day_of_week, start_time: b.start_time }))
      );
    }
  }

  // Dedupe by (name + day) so the same class on the same day isn't repeated,
  // but the same name on different days (e.g. Wed vs Sat) IS kept separate.
  // If a name appears once with a day and once without, keep the one WITH the day.
  const byKey = new Map();
  for (const it of items) {
    if (!it.name) continue;
    const key = `${it.name.toLowerCase()}|${(it.day || '').toLowerCase()}`;
    const existing = byKey.get(key);
    if (!existing || (!existing.day && it.day)) {
      byKey.set(key, it);
    }
  }
  // Sort by day-of-week then name for a stable, readable register.
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const rank = (d) => {
    const i = dayOrder.indexOf((d || '').toLowerCase());
    return i === -1 ? 99 : i;
  };
  return Array.from(byKey.values()).sort((a, b) => {
    const r = rank(a.day) - rank(b.day);
    return r !== 0 ? r : a.name.localeCompare(b.name);
  });
};

/** Comma-separated class names with the day in parentheses when known. */
const classesForStudent = (student, classLookup) => {
  const list = studentClassList(student, classLookup);
  return list
    .map((c) => (c.day ? `${c.name} (${c.day})` : c.name))
    .join(', ');
};

const csvCell = (value) => {
  if (value == null) return '';
  const s = String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const exportStudentsCsv = (students, filename = 'upa-students.csv', availableClasses = []) => {
  const classLookup = buildClassLookup(availableClasses);
  const header = [
    'Student Name',
    'Classes',
  ];
  const rows = students.map((s) => [
    s.name || s.student_name || '',
    classesForStudent(s, classLookup),
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\n');

  // BOM so Excel opens it as UTF-8 (preserves £, accents etc.)
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

export const exportStudentsPdf = (students, opts = {}) => {
  const classLookup = buildClassLookup(opts.availableClasses || []);
  const title = opts.title || 'UPA School — Student Register';
  const subtitle = opts.subtitle || '';
  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 portrait; margin: 18mm 14mm; }
  body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #111; font-size: 11pt; margin: 0; }
  h1 { font-size: 18pt; margin: 0 0 4px; color: #ff007f; }
  .subtitle { color: #555; font-size: 10pt; margin-bottom: 18px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 7px 8px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; font-size: 10pt; }
  th { background: #111; color: #fff; font-weight: 600; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.3px; }
  tr:nth-child(even) td { background: #fafafa; }
  .count { font-size: 9pt; color: #555; margin-top: 18px; }
  @media print { .no-print { display: none; } }
  .toolbar { padding: 10px; background: #f0f0f0; text-align: center; border-bottom: 1px solid #ccc; }
  .toolbar button { background: #ff007f; color: white; border: none; padding: 8px 18px; font-size: 14px; cursor: pointer; border-radius: 4px; }
</style></head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>
  <h1>${escapeHtml(title)}</h1>
  ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
  <table>
    <thead>
      <tr>
        <th style="width:45%;">Student</th>
        <th style="width:55%;">Classes</th>
      </tr>
    </thead>
    <tbody>
      ${students.map((s) => `
        <tr>
          <td>${escapeHtml(s.name || s.student_name || '')}</td>
          <td>${escapeHtml(classesForStudent(s, classLookup))}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  <div class="count">${students.length} student${students.length === 1 ? '' : 's'} · Generated ${new Date().toLocaleString('en-GB')}</div>
  <script>
    // Auto-open the print dialog when the popup loads so the user can
    // choose "Save as PDF" without an extra click.
    window.addEventListener('load', () => setTimeout(() => window.print(), 250));
  </script>
</body></html>`;

  const win = window.open('', '_blank');
  if (!win) {
    return { ok: false, error: 'Pop-up blocked. Please allow pop-ups for upaschool.com and try again.' };
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  return { ok: true };
};
